import React, { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, TrendingUp, TrendingDown, Wallet, Check } from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDate, errMsg } from "@/lib/api";

const TABS = [
  { id: "pagar", label: "Contas a pagar" },
  { id: "receber", label: "Contas a receber" },
  { id: "fluxo", label: "Fluxo de caixa" },
  { id: "dre", label: "DRE Gerencial" },
];

function AccountsTable({ kind }) {
  const isPay = kind === "pagar";
  const ep = isPay ? "payables" : "receivables";
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(null);

  const load = () => api.get(`/${ep}`).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [ep]);

  const save = async () => {
    try {
      const body = { ...modal, valor: parseFloat(modal.valor) || 0 };
      await api.post(`/${ep}`, body);
      toast.success("Lançamento criado"); setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { try { await api.delete(`/${ep}/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const marcar = async (item) => {
    const status = isPay ? "pago" : "recebido";
    try { await api.put(`/${ep}/${item.id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!list) return <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  const nameField = isPay ? "fornecedor" : "cliente";

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid={`add-${kind}-btn`} onClick={() => setModal({ status: "pendente" })} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">
          <Plus className="h-4 w-4" /> Novo lançamento
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
            <tr>
              <th className="p-3">{isPay ? "Fornecedor" : "Cliente / Órgão"}</th>
              <th className="p-3">Categoria</th><th className="p-3">Vencimento</th>
              <th className="p-3 text-right">Valor</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((it) => (
              <tr key={it.id} data-testid={`${kind}-row`} className="hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800">{it[nameField] || it.orgao}</td>
                <td className="p-3 text-slate-500">{it.categoria}</td>
                <td className="p-3 text-slate-600">{fmtDate(it.vencimento)}</td>
                <td className="p-3 text-right font-mono font-semibold">{brl(it.valor)}</td>
                <td className="p-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${it.status === "pendente" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{it.status}</span>
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    {it.status === "pendente" && <button onClick={() => marcar(it)} title="Marcar como quitado" className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-emerald-600"><Check className="h-4 w-4" /></button>}
                    <button onClick={() => del(it.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 font-display text-lg font-bold">Novo lançamento</h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">{isPay ? "Fornecedor" : "Cliente / Órgão"}</label>
                <input data-testid={`${kind}-input-name`} value={modal[nameField] ?? ""} onChange={(e) => setModal({ ...modal, [nameField]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Categoria</label>
                <input value={modal.categoria ?? ""} onChange={(e) => setModal({ ...modal, categoria: e.target.value })} placeholder={isPay ? "Ex: Custo dos produtos vendidos" : "Ex: Receita de vendas"} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Vencimento</label>
                  <input type="date" value={modal.vencimento ?? ""} onChange={(e) => setModal({ ...modal, vencimento: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Valor (R$)</label>
                  <input data-testid={`${kind}-input-valor`} type="number" value={modal.valor ?? ""} onChange={(e) => setModal({ ...modal, valor: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
              <button data-testid={`${kind}-save-btn`} onClick={save} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Fluxo() {
  const [cf, setCf] = useState(null);
  useEffect(() => { api.get("/finance/cashflow").then((r) => setCf(r.data)).catch(() => {}); }, []);
  if (!cf) return <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  const cards = [
    ["Entradas previstas", cf.entradas_previstas, TrendingUp, "text-emerald-600"],
    ["Saídas previstas", cf.saidas_previstas, TrendingDown, "text-rose-600"],
    ["Saldo previsto", cf.saldo_previsto, Wallet, cf.saldo_previsto >= 0 ? "text-emerald-600" : "text-rose-600"],
    ["A receber", cf.a_receber, TrendingUp, "text-sky-600"],
    ["A pagar", cf.a_pagar, TrendingDown, "text-amber-600"],
    ["Saldo realizado", cf.saldo_realizado, Wallet, cf.saldo_realizado >= 0 ? "text-emerald-600" : "text-rose-600"],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      {cards.map(([l, v, I, c]) => (
        <div key={l} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-500"><I className={`h-4 w-4 ${c}`} /> {l}</div>
          <div className={`font-display text-xl font-bold ${c}`}>{brl(v)}</div>
        </div>
      ))}
    </div>
  );
}

function DRE() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finance/dre").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  const rows = [
    ["Receita Bruta de Vendas", d.receita_bruta, "font-semibold"],
    ["(-) Impostos sobre vendas (6%)", -d.impostos, "text-rose-600"],
    ["(=) Receita Líquida", d.receita_liquida, "font-semibold border-t"],
    ["(-) Custo dos Produtos Vendidos", -d.cpv, "text-rose-600"],
    ["(=) Lucro Bruto", d.lucro_bruto, "font-semibold border-t"],
    ["(-) Despesas Operacionais", -d.despesas_operacionais, "text-rose-600"],
    ["(=) Resultado Operacional", d.resultado_operacional, "font-bold border-t text-base"],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([l, v, cls], i) => (
            <tr key={i} className={`${cls?.includes("border-t") ? "border-t border-slate-200" : ""}`}>
              <td className={`py-2.5 ${cls || "text-slate-700"}`}>{l}</td>
              <td data-testid={l.includes("Resultado Operacional") ? "dre-lucro-liquido-val" : undefined} className={`py-2.5 text-right font-mono ${cls || "text-slate-700"} ${v < 0 ? "text-rose-600" : ""}`}>{brl(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Financeiro() {
  const [tab, setTab] = useState("pagar");
  return (
    <div className="animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">Financeiro & DRE</h1>
        <p className="text-sm text-slate-500">Contas, fluxo de caixa e demonstrativo de resultados.</p>
      </div>
      <div className="mb-5 flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 text-sm font-medium">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`finance-tab-${t.id}`} onClick={() => setTab(t.id)} className={`rounded-md px-4 py-1.5 ${tab === t.id ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "pagar" && <AccountsTable kind="pagar" />}
      {tab === "receber" && <AccountsTable kind="receber" />}
      {tab === "fluxo" && <Fluxo />}
      {tab === "dre" && <DRE />}
    </div>
  );
}
